// components/Community/People/ReligionMultiStageSelector.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";

import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import { Religion } from "api/Community/CommunityInterfaces";
import { religionsAPI } from "api/Community/PeopleAPI";

type Props = {
  label: string;
  value: Religion[] | null | undefined;
  onChange: (religionIds: number[]) => Promise<void> | void;
  disabled?: boolean;
  helperText?: string;
};

function normalizeIds(value: Religion[] | null | undefined): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((r: any) => Number(r?.id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

const ReligionMultiStageSelector: React.FC<Props> = ({
  label,
  value,
  onChange,
  disabled,
  helperText,
}) => {
  const [religions, setReligions] = useState<Religion[]>([]);
  const [loading, setLoading] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Navigation stack of parent IDs (null = top)
  const [parentStack, setParentStack] = useState<(number | null)[]>([null]);

  // Stable selection ids
  const [selectedIdList, setSelectedIdList] = useState<number[]>(() =>
    normalizeIds(value)
  );

  useEffect(() => {
    setSelectedIdList(normalizeIds(value));
  }, [value]);

  const selectedIds = useMemo(() => new Set(selectedIdList), [selectedIdList]);

  const byId = useMemo(() => {
    const m = new Map<number, Religion>();
    religions.forEach((r) => m.set(r.id, r));
    return m;
  }, [religions]);

  const childrenByParent = useMemo(() => {
    const m = new Map<number | null, Religion[]>();
    religions.forEach((r: any) => {
      const key = r.parent_religion ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    });
    Array.from(m.entries()).forEach(([k, arr]) => {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      m.set(k, arr);
    });
    return m;
  }, [religions]);

  const currentParent = parentStack[parentStack.length - 1] ?? null;

  const currentItems = useMemo(() => {
    return childrenByParent.get(currentParent) || [];
  }, [childrenByParent, currentParent]);

  const hasChildren = (id: number) =>
    (childrenByParent.get(id)?.length || 0) > 0;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await religionsAPI.getAll();
        if (!cancelled) setReligions((data as Religion[]) || []);
      } catch (e) {
        console.error("Failed to load religions", e);
        if (!cancelled) setReligions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenMenu = (el: HTMLElement) => {
    if (disabled || loading) return;
    setAnchorEl(el);
    setParentStack([null]);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setParentStack([null]);
  };
  
  const goBack = () => {
    setParentStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  };

  const goInto = (religionId: number) => {
    setParentStack((s) => [...s, religionId]);
  };

  const selectedList = useMemo(() => {
    const items = selectedIdList
      .map((id) => byId.get(id))
      .filter(Boolean) as Religion[];
    items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return items;
  }, [selectedIdList, byId]);

  const commit = async (nextIds: number[]) => {
    setSelectedIdList(nextIds);
    await onChange(nextIds);
  };

  const toggleSelection = async (religionId: number) => {
    const next = new Set(selectedIds);
    if (next.has(religionId)) next.delete(religionId);
    else next.add(religionId);
    await commit(Array.from(next));
  };

  const removeSelection = async (religionId: number) => {
    const next = selectedIdList.filter((id) => id !== religionId);
    await commit(next);
  };

  const addRowLabel = useMemo(() => {
    if (loading) return "Loading…";
    if (selectedList.length === 0) return helperText || "Select…";
    return "Add another…";
  }, [loading, selectedList.length, helperText]);

  // EditableField-like row styling
  const rowSx = (clickable: boolean) => {
    const sx: any = {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      px: 0.75,
      py: 0.5,
      borderRadius: 1,
      cursor: clickable ? "text" : "default",
      userSelect: "none",
    };
    if (clickable) {
      sx["&:hover"] = { bgcolor: "action.hover" };
    }
    return sx;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Grid2 container alignItems="flex-start">
        {/* Left label (EditableFieldsTable style) */}
        <Grid2 size={6}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              whiteSpace: "nowrap",
              pt: 0.5,
            }}
          >
            {label}
          </Typography>
        </Grid2>

        {/* Right value area */}
        <Grid2 size={6}>
          <Stack spacing={0.5} sx={{ width: "100%" }}>
            {/* Selected rows */}
            {selectedList.map((r) => (
              <Box
                key={r.id}
                sx={rowSx(!disabled)}
                onClick={(e) => {
                  // Clicking the row opens the selector (like clicking an EditableField value)
                  if (disabled) return;
                  handleOpenMenu(e.currentTarget as HTMLElement);
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.name}
                </Typography>

                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    void removeSelection(r.id);
                  }}
                  aria-label="Remove selection"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {/* Add row always at bottom */}
            <Box
              sx={{
                ...rowSx(!disabled && !loading),
                opacity: disabled ? 0.6 : 1,
                cursor: disabled || loading ? "not-allowed" : "pointer",
              }}
              onClick={(e) => handleOpenMenu(e.currentTarget as HTMLElement)}
            >
              <Typography
                variant="body1"
                sx={{
                  flex: 1,
                  color:
                    selectedList.length === 0 ? "text.secondary" : "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {addRowLabel}
              </Typography>

              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label="Add selection"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Stack>
        </Grid2>
      </Grid2>

      {/* Multi-stage Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        keepMounted
        PaperProps={{ sx: { minWidth: 320, maxHeight: 420 } }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {parentStack.length > 1 ? (
              <IconButton size="small" onClick={goBack}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            ) : null}

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {parentStack.length <= 1
                ? "Religious Categories"
                : byId.get(currentParent as number)?.name || "Subgroup"}
            </Typography>
          </Stack>

          <IconButton size="medium" onClick={handleCloseMenu}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {currentItems.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No items.
            </Typography>
          </Box>
        ) : (
          currentItems.map((r) => {
            const selected = selectedIds.has(r.id);
            const child = hasChildren(r.id);

            return (
              <MenuItem
                key={r.id}
                dense
                onClick={() => void toggleSelection(r.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <ListItemText
                  primary={r.name}
                  secondary={selected ? "Selected" : undefined}
                  primaryTypographyProps={{
                    sx: { fontWeight: selected ? 700 : 400 },
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{ noWrap: true }}
                />

                {child ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      goInto(r.id);
                    }}
                    aria-label="Open subgroup"
                  >
                    <ArrowRightIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};

export default ReligionMultiStageSelector;
